import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Swords, Gavel, Users, Play, StopCircle, Share2, Check,
  Plus, X, Sparkles, ChevronRight, Trash2, Clock, Download
} from "lucide-react";
import { workforceApi } from "../../lib/workforce";
import api, { API } from "../../lib/api";
import BrainBadge from "../../components/workforce/BrainBadge";
import Markdown from "../../components/Markdown";

const SUGGESTED_TOPICS = [
  "Should companies replace annual reviews with continuous feedback?",
  "Is remote work more productive than in-office work?",
  "Should AI-generated content be labelled by law?",
  "Do side-projects hurt or help your day-job performance?",
];

// Curated ready-to-run debate scenarios. Clicking one pre-fills topic + suggested stances.
const DEBATE_TEMPLATES = [
  {
    slug: "product-bake-off",
    emoji: "🥊",
    name: "Product Bake-off",
    topic: "Should our next feature be a native mobile app or a browser extension?",
    stances: ["Argue FOR the mobile app", "Argue FOR the browser extension", "Devil's advocate — do neither"],
    rounds: 2,
  },
  {
    slug: "hiring-decision",
    emoji: "🎯",
    name: "Hiring Decision",
    topic: "For our next senior engineering hire, should we prioritise deep specialists or versatile generalists?",
    stances: ["Argue FOR deep specialists", "Argue FOR versatile generalists", "Pragmatic take — hybrid path"],
    rounds: 2,
  },
  {
    slug: "strategy-vs-cost",
    emoji: "💰",
    name: "Strategy vs. Cost",
    topic: "Should we invest in premium AI models across the board or optimise aggressively for the cheapest capable brain per task?",
    stances: ["Argue FOR premium-everywhere", "Argue FOR cost-optimised routing", "Optimistic take — best of both"],
    rounds: 2,
  },
];

const STANCE_SUGGESTIONS = ["Argue FOR", "Argue AGAINST", "Devil's advocate", "Optimistic take", "Pragmatic take"];

export default function DebateArena() {
  const [workers, setWorkers] = useState([]);
  const [brainMap, setBrainMap] = useState({});
  const [topic, setTopic] = useState("");
  const [debaters, setDebaters] = useState([]); // [{worker_id, stance}]
  const [judgeId, setJudgeId] = useState("");
  const [rounds, setRounds] = useState(2);

  // Running state
  const [running, setRunning] = useState(false);
  const [meta, setMeta] = useState(null); // {debate_id, debaters, judge, rounds, topic}
  const [turns, setTurns] = useState({}); // { `${round}-${worker_id}`: {content, brain_id, cost, done} }
  const [currentRound, setCurrentRound] = useState(0);
  const [judgeText, setJudgeText] = useState("");
  const [judgeDone, setJudgeDone] = useState(false);
  const [totalCost, setTotalCost] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  // Past debates
  const [past, setPast] = useState([]);
  const [selectedPast, setSelectedPast] = useState(null); // full debate doc

  // Share
  const [shareCopied, setShareCopied] = useState(false);

  const abortRef = useRef(null);

  const loadPast = useCallback(async () => {
    try {
      const r = await api.get("/workforce/debate/list").then((x) => x.data);
      setPast(r.debates || []);
    } catch (err) {
      console.error("Failed to load past debates:", err);
    }
  }, []);

  useEffect(() => {
    Promise.all([workforceApi.listWorkers(), workforceApi.listBrains()]).then(([w, b]) => {
      setWorkers(w.workers || []);
      const m = {}; (b.brains || []).forEach((x) => { m[x.id] = x; });
      setBrainMap(m);
    });
    loadPast();
  }, [loadPast]);

  const toggleDebater = (worker_id) => {
    setDebaters((prev) => {
      const idx = prev.findIndex((d) => d.worker_id === worker_id);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      if (prev.length >= 5) return prev;
      // Auto-assign stance for first two
      const stance = prev.length === 0 ? "Argue FOR" : prev.length === 1 ? "Argue AGAINST" : "";
      return [...prev, { worker_id, stance, brain_override: null }];
    });
    if (judgeId === worker_id) setJudgeId("");
  };

  const updateStance = (worker_id, stance) => {
    setDebaters((prev) => prev.map((d) => (d.worker_id === worker_id ? { ...d, stance } : d)));
  };

  const updateDebaterBrain = (worker_id, brain_id) => {
    setDebaters((prev) => prev.map((d) => (
      d.worker_id === worker_id ? { ...d, brain_override: brain_id ? { brain_id } : null } : d
    )));
  };

  const [judgeBrainId, setJudgeBrainId] = useState("");

  const canStart = topic.trim() && debaters.length >= 2 && judgeId && !running;

  const startDebate = async () => {
    if (!canStart) return;
    setErrorMsg("");
    setSelectedPast(null);
    setMeta(null);
    setTurns({});
    setCurrentRound(0);
    setJudgeText("");
    setJudgeDone(false);
    setTotalCost(0);
    setRunning(true);
    setShareCopied(false);

    const token = localStorage.getItem("token");
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(`${API}/workforce/debate/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          topic: topic.trim(),
          debaters,
          judge_worker_id: judgeId,
          judge_brain_override: judgeBrainId ? { brain_id: judgeBrainId } : null,
          rounds,
        }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch (parseErr) { /* body wasn't JSON — keep HTTP status */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n"); buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data:")) continue;
          const payload = ln.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          let ev; try { ev = JSON.parse(payload); } catch { continue; }
          if (ev.type === "meta") setMeta(ev);
          else if (ev.type === "round_start") setCurrentRound(ev.round);
          else if (ev.type === "debater_delta") {
            const key = `${ev.round}-${ev.worker_id}`;
            setTurns((t) => ({ ...t, [key]: { ...(t[key] || {}), content: ((t[key]?.content) || "") + ev.content } }));
          } else if (ev.type === "debater_done") {
            const key = `${ev.round}-${ev.worker_id}`;
            setTurns((t) => ({ ...t, [key]: { ...(t[key] || {}), brain_id: ev.brain_id, cost: ev.cost, done: true } }));
          } else if (ev.type === "debater_error") {
            const key = `${ev.round}-${ev.worker_id}`;
            setTurns((t) => ({ ...t, [key]: { ...(t[key] || {}), content: `⚠️ ${ev.error}`, done: true, error: true } }));
          } else if (ev.type === "judge_delta") setJudgeText((s) => s + ev.content);
          else if (ev.type === "judge_done") setJudgeDone(true);
          else if (ev.type === "judge_error") { setJudgeText((s) => s + `\n⚠️ ${ev.error}`); setJudgeDone(true); }
          else if (ev.type === "done") { setTotalCost(ev.total_cost); loadPast(); }
          else if (ev.type === "error") setErrorMsg(ev.error);
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setErrorMsg(e.message);
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const stopDebate = () => abortRef.current?.abort();

  const loadPastDebate = async (debate_id) => {
    setSelectedPast(null);
    const d = await api.get(`/workforce/debate/${debate_id}`).then((r) => r.data);
    setSelectedPast(d);
    setMeta(null);
  };

  const deletePastDebate = async (debate_id) => {
    if (!window.confirm("Delete this debate?")) return;
    await api.delete(`/workforce/debate/${debate_id}`);
    if (selectedPast?.debate_id === debate_id) setSelectedPast(null);
    loadPast();
  };

  const shareDebate = async () => {
    const id = selectedPast?.debate_id || meta?.debate_id;
    if (!id) return;
    try {
      const r = await api.post(`/workforce/debate/${id}/share`).then((x) => x.data);
      const url = `${window.location.origin}${r.url_path}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || "Share failed");
    }
  };

  const exportDebate = async (fmt = "md") => {
    const id = selectedPast?.debate_id || meta?.debate_id;
    if (!id) return;
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/workforce/debate/${id}/export.${fmt}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = fmt === "pdf"
        ? await res.blob()
        : new Blob([await res.text()], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `debate-${id}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || "Export failed");
    }
  };

  // Which view to show
  const isViewingPast = !!selectedPast;
  const hasLive = !!meta;
  const viewingArena = hasLive || isViewingPast;

  const activeDebate = isViewingPast ? {
    topic: selectedPast.topic,
    debaters: selectedPast.debaters,
    judge: selectedPast.judge,
    rounds: selectedPast.rounds,
  } : meta;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="debate-arena-page">
      <div className="flex items-center gap-2 mb-1">
        <Swords size={20} strokeWidth={2.5} />
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Debate Mode</div>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl font-bold mb-1">Put your workers in the ring</h1>
      <p className="text-slate-600 mb-6 max-w-2xl">
        Give one prompt to several workers. Each argues their assigned stance. A judge weighs the arguments and delivers a verdict.
      </p>

      {workers.length < 3 ? (
        <div className="brut-card p-6 text-sm">
          <div className="font-heading font-bold text-lg mb-1">You need at least 3 workers</div>
          <p className="text-slate-600 mb-3">Two debaters and one judge, minimum. Hire a few more first.</p>
          <Link to="/workforce" className="brut-btn brut-btn-outline"><Plus size={14} /> Go to My Workforce</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          {/* MAIN COLUMN */}
          <div>
            {!viewingArena ? (
              <SetupPanel
                workers={workers} brainMap={brainMap}
                topic={topic} setTopic={setTopic}
                debaters={debaters} toggleDebater={toggleDebater} updateStance={updateStance}
                updateDebaterBrain={updateDebaterBrain}
                judgeId={judgeId} setJudgeId={setJudgeId}
                judgeBrainId={judgeBrainId} setJudgeBrainId={setJudgeBrainId}
                rounds={rounds} setRounds={setRounds}
                canStart={canStart} onStart={startDebate}
              />
            ) : (
              <ArenaPanel
                debate={activeDebate}
                turns={isViewingPast ? transcriptToTurns(selectedPast) : turns}
                currentRound={isViewingPast ? selectedPast.rounds : currentRound}
                judgeText={isViewingPast ? (selectedPast.verdict?.content || "") : judgeText}
                judgeDone={isViewingPast ? true : judgeDone}
                running={running}
                brainMap={brainMap}
                totalCost={isViewingPast ? (selectedPast.total_cost || 0) : totalCost}
                onStop={stopDebate}
                onReset={() => { setMeta(null); setSelectedPast(null); setTurns({}); setJudgeText(""); }}
                onShare={shareDebate}
                onExport={exportDebate}
                shareCopied={shareCopied}
                errorMsg={errorMsg}
              />
            )}
          </div>

          {/* SIDE COLUMN */}
          <div className="space-y-4">
            {!viewingArena && (
              <div className="brut-card p-4" data-testid="debate-templates">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                  <Sparkles size={12} strokeWidth={2.5} /> Ready-to-run scenarios
                </div>
                <div className="space-y-1.5">
                  {DEBATE_TEMPLATES.map((t) => (
                    <button
                      key={t.slug}
                      onClick={() => {
                        setTopic(t.topic);
                        setRounds(t.rounds);
                        // If we have enough workers, pre-fill debaters with the recommended stances
                        if (workers.length >= t.stances.length + 1) {
                          const picks = workers.slice(0, t.stances.length);
                          setDebaters(picks.map((w, i) => ({
                            worker_id: w.worker_id, stance: t.stances[i], brain_override_id: null,
                          })));
                          setJudgeId(workers[t.stances.length]?.worker_id || workers[0].worker_id);
                        }
                      }}
                      className="w-full text-left text-sm hover:bg-amber-50 rounded-lg px-2 py-2 border-2 border-transparent hover:border-slate-900 transition-colors"
                      data-testid={`debate-template-${t.slug}`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <span>{t.emoji}</span>
                        <span className="font-heading font-bold text-slate-900">{t.name}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 leading-snug line-clamp-2">{t.topic}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!viewingArena && (
              <div className="brut-card p-4">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                  <Sparkles size={12} strokeWidth={2.5} /> Suggested topics
                </div>
                <div className="space-y-1.5">
                  {SUGGESTED_TOPICS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTopic(t)}
                      className="w-full text-left text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg px-2 py-1.5 border border-transparent hover:border-slate-300"
                      data-testid={`suggested-${t.slice(0, 8)}`}
                    >
                      <ChevronRight size={12} className="inline mr-1 text-slate-400" />{t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="brut-card p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                <Clock size={12} strokeWidth={2.5} /> Past debates
              </div>
              {past.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No debates yet.</div>
              ) : (
                <div className="space-y-1.5" data-testid="past-debates-list">
                  {past.map((d) => (
                    <div key={d.debate_id} className="group flex items-start gap-1">
                      <button
                        onClick={() => loadPastDebate(d.debate_id)}
                        className="flex-1 text-left text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg px-2 py-1.5 border border-transparent hover:border-slate-300 line-clamp-2"
                        data-testid={`past-${d.debate_id}`}
                      >
                        <div className="font-heading font-bold truncate">{d.topic}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                          {d.debaters?.length || 0} debaters · {d.rounds} rounds · ${(d.total_cost || 0).toFixed(4)}
                        </div>
                      </button>
                      <button
                        onClick={() => deletePastDebate(d.debate_id)}
                        className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 p-1"
                        data-testid={`del-past-${d.debate_id}`}
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function transcriptToTurns(debate) {
  const out = {};
  (debate.transcript || []).forEach((t) => {
    out[`${t.round}-${t.worker_id}`] = { content: t.content, brain_id: t.brain_id, cost: t.cost, done: true };
  });
  return out;
}

/* -------------------- SETUP -------------------- */
function SetupPanel({
  workers, brainMap, topic, setTopic,
  debaters, toggleDebater, updateStance, updateDebaterBrain,
  judgeId, setJudgeId, judgeBrainId, setJudgeBrainId,
  rounds, setRounds,
  canStart, onStart,
}) {
  const debaterIds = new Set(debaters.map((d) => d.worker_id));
  return (
    <div className="space-y-5" data-testid="debate-setup">
      {/* Topic */}
      <div className="brut-card p-5">
        <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">Debate topic</label>
        <textarea
          rows={2}
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g., Should companies replace annual reviews with continuous feedback?"
          className="brut-input resize-none"
          data-testid="debate-topic-input"
        />
      </div>

      {/* Debaters */}
      <div className="brut-card p-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5">
            <Users size={12} strokeWidth={2.5} /> Debaters (2-5)
          </label>
          <span className="text-xs text-slate-500 font-mono" data-testid="debater-count">{debaters.length}/5</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-2 mb-3" data-testid="worker-picker">
          {workers.map((w) => {
            const selected = debaterIds.has(w.worker_id);
            const isJudge = judgeId === w.worker_id;
            const disabled = isJudge || (!selected && debaters.length >= 5);
            const brain = brainMap[(w.brain || {}).brain_id];
            return (
              <button
                key={w.worker_id}
                disabled={disabled}
                onClick={() => toggleDebater(w.worker_id)}
                data-testid={`pick-debater-${w.worker_id}`}
                className={`text-left brut-card p-3 flex items-center gap-2.5 transition-all ${
                  selected ? "bg-amber-50 ring-2 ring-slate-900" : "bg-white hover:bg-slate-50"
                } ${disabled && !selected ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div className="w-10 h-10 rounded-xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-xl shrink-0">
                  {w.avatar || "👤"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{w.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{w.title}</div>
                  {brain && <div className="mt-0.5"><BrainBadge brain={brain} size="sm" /></div>}
                </div>
                {selected && <Check size={14} className="text-emerald-600 shrink-0" strokeWidth={3} />}
              </button>
            );
          })}
        </div>

        {debaters.length > 0 && (
          <div className="border-t border-slate-200 pt-3 space-y-2" data-testid="stance-list">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
              Stance & brain per debater (brain override optional)
            </div>
            {debaters.map((d) => {
              const w = workers.find((x) => x.worker_id === d.worker_id);
              if (!w) return null;
              const defaultBrain = brainMap[(w.brain || {}).brain_id];
              return (
                <div key={d.worker_id} className="flex items-center gap-1.5 flex-wrap">
                  <div className="flex items-center gap-1.5 w-32 shrink-0 text-sm">
                    <span>{w.avatar}</span>
                    <span className="font-heading font-bold truncate">{w.name}</span>
                  </div>
                  <input
                    value={d.stance}
                    onChange={(e) => updateStance(d.worker_id, e.target.value)}
                    placeholder="Stance…"
                    className="brut-input flex-1 min-w-[120px] py-1.5 text-xs"
                    list={`stances-${d.worker_id}`}
                    data-testid={`stance-input-${d.worker_id}`}
                  />
                  <datalist id={`stances-${d.worker_id}`}>
                    {STANCE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                  </datalist>
                  <select
                    value={d.brain_override?.brain_id || ""}
                    onChange={(e) => updateDebaterBrain(d.worker_id, e.target.value)}
                    className="brut-input py-1.5 text-xs min-w-[140px]"
                    data-testid={`brain-override-${d.worker_id}`}
                  >
                    <option value="">{defaultBrain ? `Default: ${defaultBrain.name}` : "Default brain"}</option>
                    {Object.values(brainMap).map((b) => (
                      <option key={b.id} value={b.id}>{`⚡ ${b.name}`}</option>
                    ))}
                  </select>
                  <button onClick={() => toggleDebater(d.worker_id)} className="text-slate-400 hover:text-red-500 shrink-0" data-testid={`remove-debater-${d.worker_id}`}>
                    <X size={14} strokeWidth={3} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Judge + Rounds */}
      <div className="brut-card p-5 space-y-3">
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1.5">
            <Gavel size={12} strokeWidth={2.5} /> Judge
          </label>
          <div className="grid sm:grid-cols-2 gap-2">
            <select
              value={judgeId}
              onChange={(e) => setJudgeId(e.target.value)}
              className="brut-input"
              data-testid="judge-select"
            >
              <option value="">— Pick a judge —</option>
              {workers.filter((w) => !debaterIds.has(w.worker_id)).map((w) => (
                <option key={w.worker_id} value={w.worker_id}>{`${w.avatar || "👤"} ${w.name} — ${w.title}`}</option>
              ))}
            </select>
            <select
              value={judgeBrainId}
              onChange={(e) => setJudgeBrainId(e.target.value)}
              className="brut-input"
              data-testid="judge-brain-override"
            >
              <option value="">Judge's default brain</option>
              {Object.values(brainMap).map((b) => (
                <option key={b.id} value={b.id}>{`⚡ ${b.name}`}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
            Rounds: <span className="font-mono">{rounds}</span>
          </label>
          <div className="flex gap-1.5">
            {[1, 2, 3].map((r) => (
              <button
                key={r}
                onClick={() => setRounds(r)}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-heading font-bold border-2 ${
                  rounds === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
                }`}
                data-testid={`rounds-${r}`}
              >
                {r} round{r > 1 ? "s" : ""}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-1.5">
            More rounds = deeper rebuttals but higher cost.
          </div>
        </div>
      </div>

      <button
        onClick={onStart}
        disabled={!canStart}
        className="brut-btn w-full py-3"
        data-testid="start-debate-btn"
      >
        <Play size={16} strokeWidth={2.5} /> Start the debate
      </button>
    </div>
  );
}

/* -------------------- ARENA (LIVE + PLAYBACK) -------------------- */
function ArenaPanel({
  debate, turns, currentRound, judgeText, judgeDone,
  running, brainMap, totalCost, onStop, onReset, onShare, onExport, shareCopied, errorMsg,
}) {
  const rounds = Array.from({ length: debate.rounds }, (_, i) => i + 1);
  return (
    <div className="space-y-5" data-testid="debate-arena">
      <div className="brut-card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Topic</div>
            <h2 className="font-heading text-xl md:text-2xl font-bold" data-testid="live-topic">{debate.topic}</h2>
          </div>
          <div className="flex gap-2">
            {running ? (
              <button onClick={onStop} className="brut-btn brut-btn-outline" data-testid="stop-debate">
                <StopCircle size={14} strokeWidth={2.5} /> Stop
              </button>
            ) : (
              <>
                <button onClick={onShare} className="brut-btn brut-btn-outline" data-testid="share-debate-btn">
                  {shareCopied ? <Check size={14} className="text-emerald-600" /> : <Share2 size={14} strokeWidth={2.5} />}
                  {shareCopied ? "Copied!" : "Share"}
                </button>
                {onExport && (
                  <>
                    <button onClick={() => onExport("md")} className="brut-btn brut-btn-outline" data-testid="export-debate-btn">
                      <Download size={14} strokeWidth={2.5} /> Export MD
                    </button>
                    <button onClick={() => onExport("pdf")} className="brut-btn brut-btn-outline" data-testid="export-debate-pdf-btn">
                      <Download size={14} strokeWidth={2.5} /> Export PDF
                    </button>
                  </>
                )}
                <button onClick={onReset} className="brut-btn brut-btn-outline" data-testid="new-debate-btn">
                  New debate
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {(debate.debaters || []).map((d) => (
            <div key={d.worker_id} className="flex items-center gap-2 bg-slate-50 border-2 border-slate-900 rounded-full px-3 py-1 text-xs">
              <span>{d.avatar}</span><span className="font-heading font-bold">{d.name}</span>
              {d.stance && <span className="text-slate-500">· {d.stance}</span>}
            </div>
          ))}
          {debate.judge && (
            <div className="flex items-center gap-2 bg-amber-50 border-2 border-slate-900 rounded-full px-3 py-1 text-xs">
              <Gavel size={11} strokeWidth={2.5} /><span>{debate.judge.avatar}</span>
              <span className="font-heading font-bold">{debate.judge.name}</span>
              <span className="text-slate-500 uppercase tracking-widest text-[9px]">Judge</span>
            </div>
          )}
        </div>

        {totalCost > 0 && (
          <div className="text-xs text-slate-500 mt-3 font-mono" data-testid="debate-cost">
            Total spend so far: <b>${totalCost.toFixed(4)}</b>
          </div>
        )}
        {errorMsg && (
          <div className="mt-3 p-2 rounded border-2 border-red-500 bg-red-50 text-red-700 text-xs" data-testid="debate-error">
            ⚠️ {errorMsg}
          </div>
        )}
      </div>

      {rounds.map((r) => {
        const active = running && currentRound === r;
        const shown = running ? r <= currentRound : true;
        if (!shown) return null;
        return (
          <div key={r} data-testid={`debate-round-${r}`}>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-2">
              Round {r} {active && <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 dot-blink" />}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {(debate.debaters || []).map((d) => {
                const key = `${r}-${d.worker_id}`;
                const turn = turns[key];
                return <DebaterTurn key={key} debater={d} turn={turn} brainMap={brainMap} running={running} />;
              })}
            </div>
          </div>
        );
      })}

      {(judgeText || (running && currentRound >= debate.rounds)) && (
        <div className="brut-card p-5 bg-amber-50 border-slate-900" data-testid="judge-panel">
          <div className="flex items-center gap-2 mb-3">
            <Gavel size={16} strokeWidth={2.5} />
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Verdict by</div>
              <div className="font-heading font-bold">
                {debate.judge?.avatar} {debate.judge?.name}
              </div>
            </div>
            {running && !judgeDone && <span className="ml-auto inline-block w-2 h-2 rounded-full bg-emerald-500 dot-blink" />}
          </div>
          {judgeText ? (
            <Markdown text={judgeText} />
          ) : (
            <div className="flex gap-1.5">
              <span className="w-2 h-2 rounded-full dot-blink bg-slate-500" />
              <span className="w-2 h-2 rounded-full dot-blink bg-slate-500" style={{ animationDelay: "0.2s" }} />
              <span className="w-2 h-2 rounded-full dot-blink bg-slate-500" style={{ animationDelay: "0.4s" }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DebaterTurn({ debater, turn, brainMap, running }) {
  const brain = turn?.brain_id ? brainMap[turn.brain_id] : null;
  return (
    <div className="brut-card p-4" data-testid={`turn-${debater.worker_id}`}>
      <div className="flex items-center gap-2 mb-2 text-xs">
        <span className="text-lg leading-none">{debater.avatar}</span>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold truncate">{debater.name}</div>
          {debater.stance && <div className="text-[10px] text-slate-500 uppercase tracking-widest truncate">{debater.stance}</div>}
        </div>
        {brain && <BrainBadge brain={brain} size="sm" />}
        {running && !turn?.done && turn && <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 dot-blink" />}
      </div>
      {turn?.content ? (
        <Markdown text={turn.content} />
      ) : (
        <div className="flex gap-1.5 py-1">
          <span className="w-1.5 h-1.5 rounded-full dot-blink bg-slate-400" />
          <span className="w-1.5 h-1.5 rounded-full dot-blink bg-slate-400" style={{ animationDelay: "0.2s" }} />
          <span className="w-1.5 h-1.5 rounded-full dot-blink bg-slate-400" style={{ animationDelay: "0.4s" }} />
        </div>
      )}
      {typeof turn?.cost === "number" && turn.cost > 0 && (
        <div className="text-[10px] text-slate-400 font-mono mt-2">${turn.cost.toFixed(4)}</div>
      )}
    </div>
  );
}
